import { InstantSearchProduct, ProductSibling } from './instant-search.types'
import { useSwatches } from '@/components/hooks/useSwatches'
import { useBadges } from '@/components/hooks/useBadges'
import { usePromolines } from '@/components/hooks/usePromolines'

interface ProductCardProps {
  product: InstantSearchProduct
}

declare module 'preact' {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'klaviyo-star-rating': any
    }
  }
}

export function InstantSearchProductCard({ product }: ProductCardProps) {
  const isOnSale = product.compare_at_price && product.compare_at_price > product.price
  const displayName = product.metafields?.customer_facing_product_name || product.title
  const variants = product.metafields?.siblings || []

  const badges = useBadges({
    tags: product.tags,
    user_scope: 'Show to All',
    start_date: '',
    products: [product.handle],
    collections: product.collections || []
  })

  const promolines = usePromolines({
    tags: product.tags,
    user_scope: 'Show to All',
    start_date: '',
    products: [product.handle],
    collections: product.collections || []
  })

  const formatPrice = (price: number | null) => {
    if (price === null) return
    return `$${(price / 100).toFixed(2)}`.replace('.00', '')
  }

  const renderSwatch = (variant: ProductSibling) => {
    const variantTags = Array.isArray(variant.tags) ? variant.tags : []

    const swatches = useSwatches({
      tags: variantTags,
      user_scope: 'Show to All',
      start_date: '',
      products: [],
      collections: product.collections
    })

    const swatch = swatches[0]
    const hexTag = variantTags.find(tag => tag.startsWith('Hex:'))
    let hexColor = null
    if (hexTag) {
      const parts = hexTag.split('-')
      hexColor = parts[parts.length - 1].trim()
    }

    const color = swatch?.background_colour || hexColor
    const image = swatch?.image

    const swatchContent = (
      <div class={`relative size-1.125 shrink-0 rounded-full ${variant.isCurrentProduct ? 'size-1.5 p-0.125 border-2 border-primary' : ''}`}>
        <div class="size-full rounded-full" style={color ? `background-color: ${color}` : undefined}>
          {image && <img src={image} class="size-full object-cover rounded-full" alt="" />}
        </div>
      </div>
    )

    if (variant.isCurrentProduct) {
      return swatchContent
    }

    return (
      <a
        href={`/products/${variant.handle}`}
        class="group/swatch"
        aria-label={`View ${variant.handle}`}
      >
        {swatchContent}
      </a>
    )
  }

  return (
    <div class="w-3/5 md:w-[30%] mr-gutter">
      <div class="group/card relative flex" id={`result__${product.handle}`}>
        <div class="flex flex-col gap-y-0.625 w-full grow">
          <div class="relative w-full overflow-hidden aspect-1/1">
            <a href={product.url} aria-label={displayName}>
              <div class="relative size-full overflow-hidden border-0">
                {product.images[1] && (
                  <img
                    src={typeof product.images[1] === 'string' ? `https:${product.images[1]}` : product.images[1].src}
                    alt={displayName}
                    class="absolute inset-0 object-cover object-bottom size-full bg-[--section-bg-colour,theme(colors.brand.white)]"
                    loading="lazy"
                  />
                )}
                {product.images[0] && (
                  <img
                    src={typeof product.images[0] === 'string' ? `https:${product.images[0]}` : product.images[0].src}
                    alt={displayName}
                    class="absolute inset-0 object-cover object-bottom size-full transition-all bg-[--section-bg-colour,theme(colors.brand.white)] group-hover/card:opacity-0"
                    loading="lazy"
                  />
                )}
                <div class="flex absolute pt-0.5 px-0.5 md:pt-1 md:px-1 flex-col md:flex-wrap md:flex-row items-start md:items-center gap-y-0.5 md:gap-y-0.313 md:gap-x-0.625 w-full">
                  {badges.length > 0 && (
                    <div class="flex flex-col md:flex-row md:flex-wrap gap-y-0.313">
                      {badges.map(badge => (
                        <span
                          class="font-serif paragraph-xs font-medium tracking-[-0.015rem] px-0.5 py-0.25 rounded-[80px]"
                          style={`background-color: ${badge.background_colour}; color: ${badge.text_colour}`}
                        >
                          {badge.display_name}
                        </span>
                      ))}
                    </div>
                  )}
                  {promolines.length > 0 && (
                    <div class="gap-y-0.313 hidden md:flex md:flex-wrap md:flex-row gap-x-0.625">
                      {promolines.map(promo => (
                        <span
                          class="font-serif paragraph-xs font-medium tracking-[-0.015rem] px-0.5 py-0.25 rounded-[80px]"
                          style={`background-color: ${promo.background_colour}; color: ${promo.text_colour}`}
                        >
                          {promo.display_name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </a>
          </div>
          <div class="grow flex flex-col gap-y-0.5 px-0.5 md:px-1">
            <div class="flex flex-col md:flex-row flex-nowrap md:justify-between md:items-center md-max:gap-y-0.25">
              <a
                href={product.url}
                class="product-name font-serif tracking-[-0.02rem] font-medium link-fade"
                aria-label={displayName}
              >
                {displayName}
              </a>
              <p class="flex gap-x-0.5">
                {isOnSale && (
                  <span class="font-450 line-through paragraph-s text-neutral-secondary">
                    {formatPrice(product.compare_at_price)}
                  </span>
                )}
                <span class={`font-medium paragraph-s${isOnSale ? ' text-brand-sale' : ''}`}>
                  {formatPrice(product.price)}
                </span>
              </p>
            </div>
            <div class="flex flex-col-reverse md:flex-row flex-nowrap md:justify-between md:items-center">
              {variants.length > 0 && (
                <div class="relative flex flex-wrap gap-0.125 gap-x-0.25 items-center">
                  {variants.map(variant => renderSwatch(variant))}
                </div>
              )}
              <klaviyo-star-rating
                class="flex justify-start md:justify-end pb-0.5 md:pb-0.5 pt-1 md:pt-0.5 [&.empty-klaviyo-reviews]:hidden"
                product-id={product.id.toString()}
                product-title={product.title}
                product-type={product.type}
              />
            </div>
            {promolines.length > 0 && (
              <div class="block md:hidden pt-0.5 gap-y-0.5">
                {promolines.map(promo => (
                  <span
                    class="text-xxs font-medium uppercase px-0.5 py-0.25 rounded"
                    style={`background-color: ${promo.background_colour}; color: ${promo.text_colour}`}
                  >
                    {promo.display_name}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
